import { getDefautlVoiceForExpressiveAvatar } from '@/pages/AdvancedEditor/utils/expressive-utils';
import { logger } from '@/services/logger';
import { getKnowledgeMappedDocuments, InputAgent } from '@/state/agent';
import { rootStore } from '@/state/root/store';
import { AgentKnowledgeSource, IAgent, IVoice, LLM, LLM_OPTIONS } from '@d-id/types';
import { initializePresenter } from './initializePresenter';

export const DEFAULT_EXTERNAL_ENDPOINT = 'https://api.openai.com/v1';

const findPresenterPredicate = (presenter: any, agent?: IAgent) => {
    if (!agent) {
        return false;
    }

    if (agent.presenter?.type === 'clip') {
        return presenter.id === agent.presenter?.presenter_id;
    }

    const thumbnail = agent.presenter?.thumbnail?.split('/');

    if (!thumbnail) {
        return false;
    }

    const presenterNameFromThumbnail = thumbnail[thumbnail.length - 2];
    // Note : presenter name from thumbnail may include 'sp_' prefix for default presenters with a selected background.
    const presenterNameWithoutPrefix = presenterNameFromThumbnail?.startsWith('sp_')
        ? presenterNameFromThumbnail.substring(3)
        : presenterNameFromThumbnail;

    return !presenter?.id?.includes('-removed_bg') && !!presenter?.url?.includes(presenterNameWithoutPrefix);
};

export async function loadSavedAgentState(agent: IAgent, presenters?: any[], voiceOptions?: Record<string, IVoice[]>) {
    const { agentEditorStore } = rootStore;
    agentEditorStore.resetBaseKnowledge();
    agentEditorStore.setIsEditorLoading(true);

    // TODO: think about wrapping this in a `runInAction` block, but consider the async operations inside

    let inputAgent: Partial<InputAgent> = {
        from_template: agent?.from_template,
    };
    if (agent?.id) inputAgent.id = agent.id;
    if (agent?.preview_name) inputAgent.preview_name = agent.preview_name;
    agentEditorStore.setInputAgent(prevInputAgent => ({ ...prevInputAgent, ...inputAgent }));

    const llm: Partial<LLM> = agent?.llm || {};

    agentEditorStore.setLLM(
        prevLLM =>
            ({
                ...prevLLM,
                ...llm,
                model: llm?.model ?? LLM_OPTIONS.GPT_4O_MINI,
                prompt_customization: {
                    ...llm.prompt_customization,
                    knowledge_source: agent?.llm?.prompt_customization?.knowledge_source ?? AgentKnowledgeSource.BaseKnowledge,
                },
            }) as LLM
    );
    agentEditorStore.setStarterQuestions(agent.starter_message || agent.knowledge?.starter_message || []);
    agentEditorStore.setBaseKnowledge(llm.prompt_customization?.prompt_knowledge || agent.knowledge?.base_knowledge || '');
    agentEditorStore.setInitAgentState(prevState => ({ ...prevState, knowledgeId: agent.knowledge?.id }));

    if (agent?.knowledge?.id) {
        try {
            const docs = await getKnowledgeMappedDocuments(agent.knowledge.id);
            agentEditorStore.setUploadingDocumentFiles(docs);
            agentEditorStore.setInitAgentState((prevState: any) => ({ ...prevState, knowledgeId: agent.knowledge?.id, documents: docs }));
        } catch (e) {
            logger.error(e);
        }
    }

    if (agent?.presenter) {
        const presenter = presenters?.filter(p => !p?.id?.includes('-removed_bg')).find(p => findPresenterPredicate(p, agent));

        if (presenter) {
            initializePresenter(presenter, agent, agentEditorStore);
        }
        if (agent?.presenter?.voice?.language) {
            const language = agent.presenter.voice.language;
            agentEditorStore.setTtsSelectedLanguage(language);
            if (voiceOptions) {
                const allVoices = Object.values(voiceOptions).flat();

                const selectedVoice =
                    allVoices.find(v => v.id === agent.presenter?.voice?.voice_id) || getDefautlVoiceForExpressiveAvatar(presenter);
                const voiceWithLanguage = { ...selectedVoice, language } as IVoice;

                if (selectedVoice && voiceWithLanguage) {
                    agentEditorStore.setTtsSelectedVoice(voiceWithLanguage);
                }
            }
        }
    }

    agentEditorStore.setGreetings(agent?.greetings || []);
    agentEditorStore.setTriggers(agent?.triggers || {});
    agentEditorStore.setIsEditorLoading(false);
}
